import React, { useContext } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { FiXCircle } from "react-icons/fi";
import AppContext from "../../context/AppContext";
import translations from "./Translations";

// Matches src/pages/TaskHistory/TaskRoadmapDrawer.jsx's own status-chip logic
// exactly (that file already solved this for the same task data, shown in a
// different part of the app -- Account History needs to agree with it, not
// invent its own scheme). "Completed" gets a neutral gray/slate chip;
// literally every other status (Active, Assigned, In Progress, Finished, On
// Hold, Pending, ...) shares one amber chip showing the raw status text --
// Task Roadmap doesn't color-differentiate between those, so neither do we.
const isCompletedStatus = (status) => String(status ?? "").trim().toLowerCase() === "completed";

const StatusOutcomePill = ({ success, className = "" }) => {
  const { langMode, theme } = useContext(AppContext);

  if (success) {
    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border shadow-sm ${
          theme === "dark"
            ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
            : theme === "high-contrast"
              ? "bg-emerald-600/20 border-emerald-600 text-emerald-700 high-contrast"
              : "bg-emerald-50 border-emerald-200 text-emerald-700"
        } ${className}`}
      >
        <CheckCircleIcon className="w-4 h-4 shrink-0" aria-hidden />
        {translations[langMode].Success}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border shadow-sm ${
        theme === "dark"
          ? "bg-red-500/15 border-red-500/40 text-red-400"
          : theme === "high-contrast"
            ? "bg-red-600/20 border-red-600 text-red-700 high-contrast"
            : "bg-red-50 border-red-200 text-red-700"
      } ${className}`}
    >
      <FiXCircle className="w-4 h-4 shrink-0" aria-hidden />
      {translations[langMode].Unsuccessful}
    </span>
  );
};

// Presentational only. Two call sites, two different rules:
// - TaskRoadmapDrawer.jsx calls this with only `success` (no `taskStatus`) --
//   it already renders its own status chip inline and this pill is purely the
//   separate outcome indicator shown alongside it. Untouched.
// - TaskHistory.js (Account History table) passes both. There, only ONE pill
//   is ever shown: a completed-but-failed task shows the red Unsuccessful
//   pill (the outcome overrides the status label); every other case --
//   completed-and-succeeded, or still in progress (Active/Assigned/etc.) --
//   shows the plain status chip (gray for Completed, amber otherwise).
// `className` is appended last so callers can override the default sizing
// (e.g. a compact variant inside a drawer header) without forking the pill.
const StatusPill = ({ success, taskStatus, className = "" }) => {
  const { theme } = useContext(AppContext);

  if (taskStatus) {
    const completed = isCompletedStatus(taskStatus);
    if (completed && success === false) {
      return <StatusOutcomePill success={false} className={className} />;
    }

    const chipClass = completed
      ? theme === "dark"
        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
        : theme === "high-contrast"
          ? "bg-emerald-600/20 border-emerald-600 text-emerald-700 high-contrast"
          : "bg-emerald-50 border-emerald-200 text-emerald-700"
      : theme === "dark"
        ? "bg-amber-500/15 border-amber-500/30 text-amber-400"
        : theme === "high-contrast"
          ? "bg-amber-100 border-amber-500 text-amber-800 high-contrast"
          : "bg-amber-50 border-amber-200 text-amber-700";

    return (
      <span className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm ${chipClass} ${className}`}>
        {taskStatus}
      </span>
    );
  }

  return <StatusOutcomePill success={success} className={className} />;
};

export default StatusPill;
